import { useState } from 'react';
import { capitalCase } from 'change-case';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// @mui
import { Tab, Box, Tabs, Card, Divider, Button, Stack, Typography, Paper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
// components
import useTabs from '../../../hooks/useTabs';
import Iconify from '../../../components/iconify/Iconify';
import { AssetDetailCard, AssetMaintenance, DeleteAsset } from '.';
import { NewMaintenance } from '../../maintenance/components';
import { fCurrency } from '../../../utils/formatNumber';
import { AssetAudit } from '../../audit/components';
import { NewDisposal } from '../../disposals/components';
import { useHasRole } from '../../../shared/auth/useProfile';
import { CAN_DISPOSE_ASSET, CAN_EDIT_ASSET, CAN_RAISE_MAINTENANCE } from '../../../shared/auth/roles';


export default function AssetTabs() {
  // Ask what the user may *do*, not which role they hold. These were a single
  // `isManager` flag driving an either/or: managers got New Repair + Dispose and
  // everyone else got Edit + Delete, so an admin could never raise a repair from
  // an asset (the API allows it) and auditors and data-entry users were shown
  // Edit and Delete. Derived per mount, not once at module load.
  const canRaiseMaintenance = useHasRole(CAN_RAISE_MAINTENANCE);
  const canDisposeAsset = useHasRole(CAN_DISPOSE_ASSET);
  const canEditAsset = useHasRole(CAN_EDIT_ASSET);
  const { depreciationData, asset } = useSelector((state) => state.assetReducer);
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTab, onChangeTab } = useTabs('details');
  const [openRepair, setOpenRepair] = useState(false);
  const [openDispose, setOpenDispose] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const ACCOUNT_TABS = [
    {
      value: 'details',
      icon: <InfoOutlinedIcon />,
      component: <AssetDetailCard recordId={id} />,
    },
    {
      value: 'repair history',
      icon: <Iconify icon={'ic:round-receipt'} width={20} height={20} />,
      component: <AssetMaintenance assetId={id} />,
    },
    {
      value: 'audit history',
      icon: <Iconify icon={'ic:round-receipt'} width={20} height={20} />,
      component: <AssetAudit assetId={id} />,
    },
  ];

  const handleRepair = () => {
    setOpenRepair(!openRepair);
  };
  const handleDisposal = () => {
    setOpenDispose(!openDispose);
  };
  const handleDelete = (deleted) => {
    setOpenDelete(false);
    // The asset is gone from the lists, so there is nothing left to show here.
    if (deleted) navigate('/dashboard/assets');
  };

  return (
    <>
      {openRepair && <NewMaintenance open={openRepair} handleClose={handleRepair} assetId={id} />}
      {openDispose && <NewDisposal open={openDispose} handleClose={handleDisposal} assetId={id} />}
      {openDelete && <DeleteAsset open={openDelete} handleClose={handleDelete} asset={asset} />}
      <Paper sx={{ py: 3 }} elevation="1">
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
          <Stack width={1} textAlign="center">
            <Typography variant="subtitle">DEPRECIATION(as of Today)</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              KES {fCurrency(depreciationData?.todays_depreciation)}
            </Typography>
          </Stack>

          <Stack width={1} textAlign="center">
            <Typography variant="subtitle">NETBOOK VALUE(as of Today)</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              KES {fCurrency(depreciationData?.todays_net_book_value)}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
      <Card sx={{ mt: 5 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" sx={{ float: 'right' }} spacing={1}>
            {canRaiseMaintenance && (
              <Button variant="contained" size="small" startIcon={<BuildCircleOutlinedIcon />} onClick={handleRepair}>
                New Repair
              </Button>
            )}
            {canDisposeAsset && (
              <Button variant="contained" size="small" startIcon={<SellOutlinedIcon />} onClick={handleDisposal}>
                Dispose
              </Button>
            )}
            {canEditAsset && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => navigate(`/dashboard/assets/${id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={() => setOpenDelete(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </Stack>
        </Box>
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={onChangeTab}
        >
          {ACCOUNT_TABS.map((tab) => (
            <Tab
              // disableRipple
              key={tab.value}
              label={capitalCase(tab.value)}
              iconPosition="start"
              icon={tab.icon}
              value={tab.value}
            />
          ))}
        </Tabs>
        <Divider />

        <Box sx={{ mb: 5 }} />

        {ACCOUNT_TABS.map((tab) => {
          const isMatched = tab.value === currentTab;
          return isMatched && <Box key={tab.value}>{tab.component}</Box>;
        })}
      </Card>
    </>
  );
}
